import { AdminUpdateActionEnum, BanTypeEnum, GenderEnum, UserAccountStatusEnum } from '@/lib/enum.ts';
import { DropdownMenuSeparator, DropdownMenuGroup, DropdownMenuItem } from '@/components/ui';
import { BadgeX, MailX, Smartphone, TriangleAlert, X } from 'lucide-react';
import { BadgeUserCertified, LabelGender } from '@/components/custom';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';

import imgVenusIcon from '@/assets/images/svg/venus-icon.svg';
import imgMarsIcon from '@/assets/images/svg/mars-icon.svg';
import { notify, notifyInternalMessage } from '@/functions';
import { broadcastService } from '@/services';

import type { IUserData } from '@/lib/interface';
import { useRequestDeleteUser, useRequestEditUser, useRequestWarnUser, useRequestBanUser } from '@/hooks/httpRequest';
import { isBannedOrDeleted, isCertifPending, canBanDevice, isCertified } from '@/lib/utils.ts';

type TProps = {
	disableInvalidateQueries?: boolean;
	user: IUserData;
	removeItemLocally?: (userId: string) => void;
};

const ButtonActionsOnUserItems = ({ user, disableInvalidateQueries, removeItemLocally }: TProps) => {
	const { triggerDeleteUser, isLoadingDeleteUser } = useRequestDeleteUser({ disableInvalidateQueries });
	const { triggerWarnUser, isLoadingWarnUser } = useRequestWarnUser({ disableInvalidateQueries });
	const { triggerEditUser, isLoadingEditUser } = useRequestEditUser({ disableInvalidateQueries });
	const { triggerBanUser, isLoadingBanUser } = useRequestBanUser({ disableInvalidateQueries });

	const updateUser = async (action: AdminUpdateActionEnum) => {
		let msg = '';

		if (action === AdminUpdateActionEnum.CHANGE_GENDER) {
			msg = 'Etes-vous sur de vouloir changer son genre ?';
		} else if (action === AdminUpdateActionEnum.REMOVE_CERTIFICATION) {
			msg = 'Etes-vous sur de vouloir supprimer sa certification ?';
		} else if (action === AdminUpdateActionEnum.REQUEST_CERTIF) {
			msg = 'Etes-vous sur de vouloir exiger la certification de ce profil ?';
		}

		const yes = await confirm(msg);

		if (!yes) {
			return;
		}

		const nextGender = user.gender === GenderEnum.MALE ? GenderEnum.FEMALE : GenderEnum.MALE;

		if (action === AdminUpdateActionEnum.CHANGE_GENDER) {
			broadcastService.events.publish({
				type: 'userGenderChangedOptimistic',
				data: {
					userId: user._id,
					newGender: nextGender
				}
			});
		}
		if (action === AdminUpdateActionEnum.REQUEST_CERTIF) {
			broadcastService.events.publish({
				type: 'userCertificationRequiredOptimistic',
				data: {
					userId: user._id
				}
			});
		}

		triggerEditUser({ userId: user?._id, action })
			.then(() => notify('Profil mis à jour.'))
			.catch(error => {
				if (action === AdminUpdateActionEnum.CHANGE_GENDER || action === AdminUpdateActionEnum.REQUEST_CERTIF) {
					window.location.reload();
				}
				notifyInternalMessage(error);
			});
	};

	const onWarnPress = async () => {
		const yes = await confirm('Etes-vous sur de vouloir avertir cet utilisateur ?');

		if (!yes) {
			return;
		}

		triggerWarnUser({ warnUserId: user?._id })
			.then(() => notify('Profil a été averti.'))
			.catch(notifyInternalMessage);
	};

	const onBanPress = async (banType: BanTypeEnum) => {
		let msg = '';
		if (banType === BanTypeEnum.DEVICE_BAN) {
			msg = 'Etes-vous sur de vouloir bannir son téléphone ? Cet utilisateur ne pourra plus jamais utiliser Heyama';
		} else if (banType === BanTypeEnum.EMAIL_BAN) {
			msg = 'Etes-vous sur de vouloir bannir son adresse email ?';
		}

		const yes = await confirm(msg);

		if (!yes) {
			return;
		}

		removeItemLocally?.(user._id);
		triggerBanUser({ userId: user._id, banType })
			.then(() => notify('Le profil a été banni.'))
			.catch(notifyInternalMessage);
	};

	const onDeleteAccountPress = async () => {
		const yes = await confirm('Etes-vous sur de vouloir supprimer son compte ?');
		if (!yes) return;

		removeItemLocally?.(user._id);
		await triggerDeleteUser({ userId: user?._id })
			.then(() => notify('Le profil a été spprimé avec succès.'))
			.catch(notifyInternalMessage);
	};

	if (!user) {
		return null;
	}

	return (
		<>
			<DropdownMenuGroup>
				{!isBannedOrDeleted(user.accountStatus) && (
					<DropdownMenuItem
						className="cursor-pointer"
						disabled={isLoadingEditUser}
						onClick={() => updateUser(AdminUpdateActionEnum.CHANGE_GENDER)}
					>
						{user.gender === GenderEnum.FEMALE && <img alt="Passer au genre masculin" src={imgMarsIcon} className="h-4" />}
						{user.gender === GenderEnum.MALE && <img alt="Passer au genre feminin" src={imgVenusIcon} className="h-4" />}
						<div className="flex items-center space-x-1">
							<span>Changer le sexe :</span>
							<LabelGender gender={user.gender === GenderEnum.FEMALE ? GenderEnum.MALE : GenderEnum.FEMALE} />
						</div>
					</DropdownMenuItem>
				)}
				{!isBannedOrDeleted(user.accountStatus) && !isCertified(user.verifications) && (
					<DropdownMenuItem
						onClick={() => updateUser(AdminUpdateActionEnum.REQUEST_CERTIF)}
						className="cursor-pointer"
						disabled={
								isLoadingEditUser ||
								user.accountStatus === UserAccountStatusEnum.SUSPENDED ||
								!!user.incognito ||
								user.extra?.certificationRequired
							}
					>
						<BadgeUserCertified forceDisplay size={16} />
						{user.extra?.certificationRequired
							? 'Certification exigée'
							: user.incognito
								? 'Exiger la certification (Incognito)'
								: isCertifPending(user.verifications)
									? 'Certification en cours'
									: 'Exiger la certification'}
					</DropdownMenuItem>
				)}
				{!isBannedOrDeleted(user.accountStatus) && (
					<DropdownMenuItem className="cursor-pointer" disabled={isLoadingWarnUser || user.accountStatus === UserAccountStatusEnum.SUSPENDED} onClick={onWarnPress}>
						<TriangleAlert size={16} />
						Avertir
					</DropdownMenuItem>
				)}
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				{!isBannedOrDeleted(user.accountStatus) && (
					<DropdownMenuItem className="cursor-pointer" disabled={isLoadingDeleteUser} onClick={onDeleteAccountPress}>
						<X size={16} />
						Supprimer son compte
					</DropdownMenuItem>
				)}
				{!isBannedOrDeleted(user.accountStatus) && isCertified(user.verifications) && (
					<DropdownMenuItem
						onClick={() => updateUser(AdminUpdateActionEnum.REMOVE_CERTIFICATION)}
						className="cursor-pointer"
						disabled={isLoadingEditUser}
					>
						<BadgeX className="text-red-500" size={16} />
						Supprimer sa certification
					</DropdownMenuItem>
				)}
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem
					className="cursor-pointer"
					disabled={isLoadingBanUser}
					onClick={() => onBanPress(BanTypeEnum.EMAIL_BAN)}
				>
					<MailX size={16} />
					Bannir son email
				</DropdownMenuItem>
				{canBanDevice(user.deviceId) && (
					<DropdownMenuItem className="cursor-pointer text-destructive" onClick={() => onBanPress(BanTypeEnum.DEVICE_BAN)}>
						<Smartphone size={16} />
						Bannir son téléphone
					</DropdownMenuItem>
				)}
			</DropdownMenuGroup>
		</>
	);
};

export default ButtonActionsOnUserItems;
